import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { authClient } from '@/services/client'
import type { AuthUser, AuthTokenResponse, PkceParams, Role } from '@/types/auth.types'
import { config } from '@/config'

// Génération PKCE (S256)
async function generatePkce(): Promise<PkceParams> {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  const codeVerifier = btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')

  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  const codeChallenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')

  const stateArray = new Uint8Array(16)
  crypto.getRandomValues(stateArray)
  const state = btoa(String.fromCharCode(...stateArray)).replace(/[^a-zA-Z0-9]/g, '')

  return { codeVerifier, codeChallenge, state }
}

export const useAuthStore = defineStore('auth', () => {
  // accessToken uniquement en mémoire (pas localStorage — protection XSS)
  const accessToken = ref<string | null>(null)
  const user = ref<AuthUser | null>(null)
  const roles = ref<Role[]>([])
  const isInitialized = ref(false)

  const isAuthenticated = computed(() => accessToken.value !== null && user.value !== null)
  const hasRole = (role: Role) => roles.value.includes(role)
  const hasAnyRole = (requiredRoles: Role[]) => requiredRoles.some(r => roles.value.includes(r))
  const isInfraOperator = computed(() => roles.value.includes('infra-operator'))
  const isReleaser = computed(() => roles.value.includes('releaser') || isInfraOperator.value)

  function setTokens(data: AuthTokenResponse): void {
    accessToken.value = data.accessToken
    user.value = data.user
    roles.value = data.user.roles
    isInitialized.value = true
  }

  function clearAuth(): void {
    accessToken.value = null
    user.value = null
    roles.value = []
  }

  async function checkBackendHealth(): Promise<void> {
    try {
      // Toute réponse HTTP (même 4xx) confirme que le backend est accessible
      await fetch('/api/services', { signal: AbortSignal.timeout(3000) })
    } catch {
      throw new Error('Le serveur Beewize Harbor est inaccessible. Vérifiez que le backend est démarré (port 4000).')
    }
  }

  async function initPkce(returnPath = '/'): Promise<void> {
    const clientId = config.authClientId
    if (!clientId || clientId.startsWith('cid_newbees_ship')) {
      throw new Error('AUTH_CLIENT_ID non configuré. Vérifiez les variables d\'environnement.')
    }

    await checkBackendHealth()

    const pkce = await generatePkce()
    sessionStorage.setItem('pkce_verifier', pkce.codeVerifier)
    sessionStorage.setItem('pkce_state', pkce.state)
    sessionStorage.setItem('pkce_return', returnPath)

    const authFrontUrl = config.authFrontUrl
    const redirectUri = config.redirectUri

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      code_challenge: pkce.codeChallenge,
      code_challenge_method: 'S256',
      state: pkce.state,
    })

    window.location.href = `${authFrontUrl}/login?${params.toString()}`
  }

  async function handleCallback(code: string, state: string): Promise<string> {
    const storedState = sessionStorage.getItem('pkce_state')
    const codeVerifier = sessionStorage.getItem('pkce_verifier')
    const returnPath = sessionStorage.getItem('pkce_return') ?? '/'

    if (!codeVerifier || state !== storedState) {
      throw new Error('État PKCE invalide')
    }

    sessionStorage.removeItem('pkce_verifier')
    sessionStorage.removeItem('pkce_state')
    sessionStorage.removeItem('pkce_return')

    const redirectUri = config.redirectUri

    const response = await authClient.post('/api/auth/token', {
      code,
      codeVerifier,
      redirectUri,
    })

    // Le backend NestJS enveloppe la réponse dans { success, data }
    const payload: AuthTokenResponse = response.data?.data ?? response.data
    setTokens(payload)
    return returnPath
  }

  let _refreshTimer: ReturnType<typeof setInterval> | null = null
  let _isRefreshing = false

  async function refreshToken(): Promise<boolean> {
    if (_isRefreshing) return true
    _isRefreshing = true
    try {
      // Le refreshToken est en httpOnly cookie — requête same-origin via proxy nginx
      const axios = (await import('axios')).default
      const response = await axios.post('/api/auth/refresh', {}, { withCredentials: true, timeout: 10000 })
      const payload: AuthTokenResponse = response.data?.data ?? response.data
      setTokens(payload)
      return true
    } catch {
      clearAuth()
      return false
    } finally {
      _isRefreshing = false
    }
  }

  async function initFromSession(): Promise<void> {
    if (isInitialized.value) return
    isInitialized.value = true

    // Tenter un refresh silencieux au démarrage (le cookie httpOnly est envoyé automatiquement)
    await refreshToken()
  }

  // Refresh proactif toutes les 10 min + au retour sur l'onglet
  function startTokenRefresh(): void {
    // Timer périodique
    _refreshTimer = setInterval(
      () => {
        if (isAuthenticated.value) refreshToken()
      },
      10 * 60 * 1000,
    )

    // Refresh immédiat quand l'utilisateur revient sur l'onglet
    document.addEventListener('visibilitychange', _onVisibilityChange)
  }

  function stopTokenRefresh(): void {
    if (_refreshTimer !== null) {
      clearInterval(_refreshTimer)
      _refreshTimer = null
    }
    document.removeEventListener('visibilitychange', _onVisibilityChange)
  }

  function _onVisibilityChange(): void {
    if (document.visibilityState === 'visible' && isAuthenticated.value) {
      refreshToken()
    }
  }

  function logout(): void {
    stopTokenRefresh()
    clearAuth()
    window.location.href = `${config.authFrontUrl}/logout?redirect_uri=${encodeURIComponent(window.location.origin)}`
  }

  return {
    accessToken,
    user,
    roles,
    isInitialized,
    isAuthenticated,
    isInfraOperator,
    isReleaser,
    hasRole,
    hasAnyRole,
    initPkce,
    handleCallback,
    refreshToken,
    initFromSession,
    startTokenRefresh,
    stopTokenRefresh,
    logout,
  }
})
